import { sourceExcerpt } from './source-excerpt';
import { propertyRecordKindLabel, propertySourceLabel } from '../field-labels';
import { formatPropertyAddress, formatPropertyAddressText } from '../presentation';
import type { IssueFacts, IssuePriority } from '../issues/types';

/**
 * What the email decides deterministically from the evidence (docs/email-design.md):
 * how a record is named, what it is about, where it is, its status change, what
 * Ava checked, the least urgent verdict the evidence allows, the button's verb
 * and the subject. The writer adds judgement and context on top; it never
 * decides these.
 */

/** The verdict a chip states in words. */
export const notificationVerdicts = ['no_action', 'heads_up', 'action_needed'] as const;
export type NotificationVerdictWord = (typeof notificationVerdicts)[number];
export const notificationVerdictLabels = {
  no_action: 'No action needed',
  heads_up: 'Heads up',
  action_needed: 'Action needed',
} as const satisfies Record<NotificationVerdictWord, string>;
const verdictRank: Record<NotificationVerdictWord, number> = {
  no_action: 0,
  heads_up: 1,
  action_needed: 2,
};
export function strongerVerdict(a: NotificationVerdictWord, b: NotificationVerdictWord) {
  return verdictRank[a] >= verdictRank[b] ? a : b;
}

/** The evidence a presentation helper reads: one frozen notification entry. */
export type PresentableEntry = {
  propertyId: string;
  issueIdentity: string | null;
  source: string;
  recordKind: string | null;
  sourceRecordId: string | null;
  title: string;
  priority: IssuePriority;
  before?: IssueFacts | null;
  after?: IssueFacts | null;
  transitions: string[];
};

/**
 * The least urgent verdict the evidence allows. A structured deadline inside a
 * week always needs the reader; one inside a month, a record the source stopped
 * listing, or a source Ava could not reach is at least worth a heads up. The
 * writer may raise a verdict, never lower it below this floor.
 */
export function verdictFloor(
  entries: Array<
    Pick<PresentableEntry, 'priority'> & Partial<Pick<PresentableEntry, 'transitions'>>
  >,
): NotificationVerdictWord {
  if (entries.some((entry) => entry.priority === 'now')) return 'action_needed';
  const unsettled = (entry: (typeof entries)[number]) =>
    entry.transitions?.includes('source_lost') || entry.transitions?.includes('record_not_listed');
  if (entries.some((entry) => entry.priority === 'soon' || unsettled(entry))) return 'heads_up';
  return 'no_action';
}

/**
 * The fallback writer's verdict, from evidence alone: it cannot judge meaning, so
 * an open record that changed is a heads up and a closed or quiet one needs nothing.
 */
export function fallbackVerdict(entries: PresentableEntry[]): NotificationVerdictWord {
  const attention = entries.some((entry) => entry.priority === 'review');
  return strongerVerdict(verdictFloor(entries), attention ? 'heads_up' : 'no_action');
}

/** Ava's still pose for an email: the avatar carries the state, the chip the verdict. */
export function avaStateFor(
  verdict: NotificationVerdictWord,
  tone: 'good_news' | 'action' | 'unclear' | 'update',
): 'watching' | 'needs-you' | 'not-sure' {
  if (verdict !== 'action_needed') return 'watching';
  return tone === 'unclear' ? 'not-sure' : 'needs-you';
}

/** The street line of an address, with its unit, without city, state or ZIP. */
export function formatPropertyStreet(address: string) {
  const formatted = formatPropertyAddress(address);
  return (formatted.split(',')[0] ?? formatted).trim() || formatted;
}

const lowerFirst = (label: string) =>
  /^[A-Z0-9]{2,}/.test(label) ? label : label.charAt(0).toLowerCase() + label.slice(1);

/** A status word as a reader sees it: "FILED" becomes "Filed", "WORK_ACCOUNTING" "Work accounting". */
export function displayStatus(value: string) {
  const text = value.trim().replace(/_/g, ' ').replace(/\s+/g, ' ');
  if (!/[a-z]/.test(text) && /[A-Z]/.test(text))
    return text.charAt(0) + text.slice(1).toLowerCase();
  return text;
}

const placementPrefix =
  /^(?:Listed at this address|On this block\s*[—–-]\s*not confirmed at this property)\s*:\s*/i;

/** How Ava names a record: its kind and official number, or the source for a coverage change. */
export function recordName(entry: PresentableEntry) {
  if (entry.issueIdentity === null) return propertySourceLabel(entry.source);
  const kind = propertyRecordKindLabel(entry.recordKind ?? 'record');
  return entry.sourceRecordId ? `${kind} ${entry.sourceRecordId}` : kind;
}

/** The record's kind as a noun in running text: "building permit", "SF311 report". */
export function recordNoun(entry: Pick<PresentableEntry, 'recordKind' | 'issueIdentity'>) {
  if (entry.issueIdentity === null || !entry.recordKind) return 'record';
  return lowerFirst(propertyRecordKindLabel(entry.recordKind));
}

/**
 * What the record is about, in a short line: its own description, never only a
 * number, so a reader with hundreds of properties can place it. Null when the
 * source gives no description.
 */
export function recordAbout(entry: PresentableEntry) {
  if (entry.issueIdentity === null) return null;
  if (sourceExcerpt(entry.title).omitted) return null;
  const title = entry.title.replace(placementPrefix, '').trim();
  // A long title is the record's own text ("On September 15, 2017, we identified your
  // building..."), not a name; the writer's summary says what it is about instead.
  if (Array.from(title).length > 90) return null;
  const kind = propertyRecordKindLabel(entry.recordKind ?? 'record');
  if (!title || title === kind || title === `${kind} ${entry.sourceRecordId ?? ''}`.trim())
    return null;
  const readable = !/[a-z]/.test(title) && /[A-Z]{3}/.test(title) ? displayStatus(title) : title;
  return excerpt(formatPropertyAddressText(readable), 120);
}

/** Where the record is relative to the property, when the source says. */
export function recordWhere(entry: PresentableEntry) {
  const scope = [entry.after, entry.before]
    .map((facts) => (facts && typeof facts.locationScope === 'string' ? facts.locationScope : null))
    .find(Boolean);
  if (scope === 'address') return 'At your address';
  if (scope === 'block') return 'On your block, not confirmed at your building';
  if (/^Listed at this address\s*:/i.test(entry.title)) return 'At your address';
  if (/^On this block/i.test(entry.title)) return 'On your block, not confirmed at your building';
  return null;
}

/** The record's status change as two words for the pills, when the source changed it. */
export function recordChange(entry: PresentableEntry) {
  const transition = entry.transitions.find((value) => value.includes('→'));
  if (!transition || sourceExcerpt(transition).omitted) return null;
  const [before, after] = transition.split('→').map((value) => excerpt(displayStatus(value), 40));
  if (!before || !after || before === after) return null;
  return { label: 'Status', before, after };
}

/**
 * What Ava checked, as real steps with results: each source she compared, and what
 * she found. Built from the frozen evidence, never written by the model.
 */
export function notificationChecks(
  entries: PresentableEntry[],
  address: string,
  displayName?: string,
) {
  // A source Ava could not reach this time is never also listed as checked.
  const reached = entries.filter((entry) => !entry.transitions.includes('source_lost'));
  const sources = [...new Set(reached.map((entry) => propertySourceLabel(entry.source)))].slice(
    0,
    2,
  );
  const rows = sources.map((label) => `Checked ${label} against my last check`);
  const records = entries.filter((entry) => entry.issueIdentity !== null);
  const street = displayName || formatPropertyStreet(address);
  if (records.length)
    rows.push(
      records.length === 1
        ? `Found 1 changed record for ${street}`
        : `Found ${records.length} changed records for ${street}`,
    );
  const lost = entries.filter((entry) => entry.transitions.includes('source_lost'));
  if (lost.length) rows.push(`Could not reach ${propertySourceLabel(lost[0]!.source)} this time`);
  return rows.slice(0, 4);
}

/**
 * The button's verb, specific to the email: what the reader does with the record.
 * The writer's action picks the verb for corrections and unclear updates.
 */
export function notificationButtonLabel(input: {
  verdict: NotificationVerdictWord;
  action: 'updates' | 'inspections' | 'correction' | 'unclear';
  entries: PresentableEntry[];
}) {
  if (input.action === 'correction') return 'Review the correction';
  if (input.action === 'inspections') return 'Review the inspection results';
  if (input.action === 'unclear') return 'Review the unexplained update';
  const records = input.entries.filter((entry) => entry.issueIdentity !== null);
  if (records.length > 1) return `Review the ${records.length} changes`;
  if (!records.length) return 'Open the property';
  return `${input.verdict === 'no_action' ? 'View' : 'Review'} the ${recordNoun(records[0]!)}`;
}

const chipWords =
  '(?:no (?:further )?action (?:is )?(?:needed|required)|action (?:is )?(?:needed|required)|heads[- ]up)';
// A chip word only comes off when punctuation or the end follows it: "Action needed on
// the permit" keeps its words rather than losing its verb.
const verdictLead = new RegExp(`^\\s*${chipWords}\\s*(?:[!.:,;\\-–—]+\\s*|$)`, 'i');
const verdictTail = new RegExp(`\\s*[,;:\\-–—]\\s*${chipWords}\\s*[!.]*\\s*$`, 'i');

/**
 * The subject's short description, cleaned of what the platform adds itself: a
 * leading verdict word and the property, which the prefix already names.
 */
export function subjectDescription(value: string, addresses: string[]) {
  let text = value.trim().replace(verdictLead, '').replace(verdictTail, '');
  for (const address of addresses) {
    for (const name of new Set([
      address,
      formatPropertyAddress(address),
      formatPropertyStreet(address),
    ])) {
      if (!name) continue;
      const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      text = text
        .replace(new RegExp(`^${escaped}\\s*[:,\\-–—]\\s*`, 'i'), '')
        .replace(new RegExp(`\\s+(?:at|for|on|in)\\s+${escaped}(?=$|[.,;:])`, 'i'), '');
    }
  }
  text = text.replace(/[.;:,\s]+$/, '').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/** A property email's subject: the property's street line, a colon, the short description. */
export function propertySubject(address: string, description: string) {
  return `${formatPropertyStreet(address)}: ${description}`;
}

/**
 * The digest's subject: the group's name in place of the property, then the counts.
 * It counts the other updates only when there are some, and says nothing needs the
 * reader only when every change was read.
 */
export function digestSubject(name: string, needs: number, others: number, complete = true) {
  const rest = `${others} ${others === 1 ? 'update' : 'updates'}`;
  if (needs) {
    const ask = `${name}: ${needs} ${needs === 1 ? 'needs' : 'need'} you`;
    return others ? `${ask}, ${rest}` : ask;
  }
  return complete ? `${name}: ${rest}, nothing needs you` : `${name}: ${rest}`;
}

function excerpt(value: string, limit: number) {
  // City text is untrusted: control characters never reach a frozen brief.
  // eslint-disable-next-line no-control-regex
  const normalized = value.replace(/[\u0000-\u001f\u007f-\u009f\s]+/g, ' ').trim();
  const characters = Array.from(normalized);
  if (characters.length <= limit) return normalized;
  const cut = characters.slice(0, limit - 1).join('');
  const word = cut.replace(/\s+\S*$/, '');
  return `${word.length > limit / 2 ? word : cut}…`;
}
